import React from 'react';
import {

  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,

  Paper,

  Typography,
  useMediaQuery,

} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { observer } from 'mobx-react';
import SignatureCanvas from 'react-signature-canvas';
import { useTamariskPaymentsStore } from '../hooks/useTamariskPaymentsStore';
import { ta } from 'date-fns/locale';
import { TitleComp } from 'libs/feature-borrowers/src/caf/components/titleComp';
import { formatDate } from 'libs/ui/src/lib/components/borrowerBaseballCard/utils';
import { useBorrowerPortalMediaQueries } from '@roc/feature-app-core';
import { get } from 'node:http';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    docuSignIframe: {
      border: 0,
      height: 'calc(100vh - 170px)',
      width: '100%',
      [theme.breakpoints.down(960)]: {
        height: 'calc(100vh - 110px)',
      },
      '&.success-page': {
        height: '280px',
        [theme.breakpoints.down(838)]: {
          height: '490px',
        },
      },
    },
    sigCanvas: {
      width: '100%',
      height: 200,
      background: '#f7f7f7',
      border: '1px solid #aaa',
      borderRadius: 16,
    },
    button: {
      width: '160px',
      marginBottom: '50px',
    },
  })
);

interface Props {
  onClose: () => void;
  open: boolean;
  successPage: boolean;
}

const TamariskDocSignModal = observer((props: Props) => {
  const { tamariskPaymentsStore } = useTamariskPaymentsStore();
  const { onClose } = props;
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { isLargeScreen } = useBorrowerPortalMediaQueries();

  const sigCanvas = React.useRef<SignatureCanvas>(null);
  const [signed, setSigned] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);

  const generateDocument = () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      return;
    }

    const signatureDataURL = sigCanvas.current.toDataURL('image/png');

    tamariskPaymentsStore.generateAppraisalPaymentDocument(signatureDataURL);

    onClose();
  };

  return (
    <Dialog
      disableEscapeKeyDown
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      open={props.open}
      scroll="paper"
      onClose={onClose}
      aria-labelledby="tamarisk-document-dialog"
    >
      <DialogTitle disableTypography>
        <Typography variant="h4">Payment Disclosure</Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <Box m={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <Grid xs={12}>
              <Box mb={1}>
                <TitleComp title="Acknowledgments" />
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12}>
            <Grid item xs={12}>
              <Box mb={3}>
                <Paper
                  variant="outlined"
                  style={{
                    padding: 24,
                    overflowY: 'auto',
                    borderColor: '#ccc',
                    maxHeight: isLargeScreen ? 300 : 150,
                  }}
                >
                  <Typography
                    variant="body1"
                    style={{
                      fontStyle: 'italic',
                    }}
                  >
                    <ul style={{ marginLeft: '16px', listStyleType: 'none' }}>
                      <li>Purpose: Appraisal</li>
                      <li style={{ marginBottom: '12px' }}>
                        Property Address(es):{' '}
                        {tamariskPaymentsStore.getPropertyAdresses()}
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        This document authorizes Tamarisk Appraisals LLC to
                        process the ACH payment or charge the credit card
                        according to the terms outlined in the related appraisal
                        (or similar) agreement. This payment authorization is
                        for the goods/services described in such appraisal (or
                        related) agreement, for the amount indicated therein and
                        is valid for one-time use only.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        I certify that: (1) I am an authorized user of the
                        credit card and that I will not dispute the payment with
                        my credit card company; as long as the credit card
                        transaction corresponds to the terms indicated in the
                        [Appraisal Agreement], (2) I am an authorized user of
                        the bank account provided and I will not dispute the
                        payment with my bank, as long as the payment corresponds
                        to the terms indicated in the [Appraisal Agreement]; (3)
                        I am a duly authorized representative of the undersigned
                        borrower entity and have been duly authorized by such
                        entity to execute this authorization on behalf of such
                        entity, and (4) The information I have provided is
                        accurate and correct and I acknowledge and assume
                        responsibility for any errors resulting from
                        incorrect/inaccurate information provided.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        *Any request already completed by an appraiser cannot be
                        cancelled. A minimum $50 cancellation fee will be
                        charged prior to the appraisal inspection date. If the
                        appraisal is cancelled on/after the date of inspection,
                        a minimum $150 cancellation fee will be charged.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        **Tamarisk Appraisals is unable to adjust fees based
                        upon appraised value.
                      </li>
                      <li style={{ marginBottom: '12px' }}>
                        To accept and agree to the above authorization of credit
                        card payment, you should sign below:
                      </li>
                    </ul>
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  color="primary"
                />
              }
              label="I have read and agree to the terms above"
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <Grid xs={12}>
              <Box my={3}>
                <TitleComp
                  title={
                    <span>
                      Signature
                      <Box color="red" component={'span'} ml={0.5}>
                        *
                      </Box>
                    </span>
                  }
                />
              </Box>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                <SignatureCanvas
                  penColor="green"
                  canvasProps={{ className: classes.sigCanvas }}
                  ref={sigCanvas}
                  onEnd={() => setSigned(true)}
                />
                <Box
                  display={'flex'}
                  justifyContent={'space-between'}
                  width="100%"
                >
                  <Button
                    size="small"
                    onClick={() => {
                      sigCanvas.current?.clear();
                      setSigned(false);
                    }}
                  >
                    Clear
                  </Button>
                  <Box color={'blue'}>{formatDate(new Date())}</Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                className={classes.button}
                onClick={() => generateDocument()}
                disabled={!agreed || !signed || sigCanvas.current?.isEmpty()}
              >
                SUBMIT
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
});

export default TamariskDocSignModal;
